/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { InformativeIcon } from '@coursera/cds-icons';
import type { SectionName } from '@coursera/event-pulse-types';

import Rating from 'bundles/unified-description-page-common/components/shared/Rating';
import InstructorItem from 'bundles/unified-description-page-common/components/shared/instructors/InstructorItem';
import Modal from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type {
  InstructorFragment,
  PartnerFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import getRelatedPartnerName from 'bundles/unified-description-page-common/utils/getRelatedPartnerName';

import _t from 'i18n!nls/xdp';

const styles = {
  subsection: css`
    & + & {
      border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      margin-top: var(--cds-spacing-300);
      padding-top: var(--cds-spacing-300);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200) 0;
    }
  `,
  subsectionHeader: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  ratings: css`
    display: flex;
    gap: var(--cds-spacing-200);
    align-items: center;
  `,
  ratingsLabel: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  linkButton: css`
    padding: 0;
    margin-left: 0;
    height: unset;
    width: unset;

    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
  item: css`
    & + & {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

interface Props {
  averageInstructorRating?: number | null;
  instructorRatingCount?: number | null;
  instructor: InstructorFragment;
  partner: PartnerFragment;
  setModalOpen: () => void;
  isModalOpen: boolean;
  trackingLocation?: SectionName;
}

const InstructorAndPartnerInfoGoogle = ({
  averageInstructorRating,
  instructorRatingCount,
  instructor,
  partner,
  setModalOpen,
  isModalOpen,
  trackingLocation,
}: Props) => {
  return (
    <div css={styles.subsection}>
      <div css={styles.subsectionHeader}>
        <Typography2 variant="subtitleMedium" component="h3">
          <FormattedMessage
            message={_t('{numberOfInstructors, plural, =1 {Instructor} other {Instructors}}')}
            numberOfInstructors={1}
          />
        </Typography2>
        {Boolean(averageInstructorRating) && (
          <div css={styles.ratings}>
            <div css={styles.ratingsLabel}>
              <Typography2 variant="bodySecondary" component="span">
                {_t('Instructor ratings')}
              </Typography2>
              <Button
                variant="ghost"
                css={styles.linkButton}
                onClick={setModalOpen}
                aria-label={_t('Information about instructor ratings')}
              >
                <InformativeIcon size="small" />
              </Button>
              <Modal header={_t('Instructor ratings')} open={isModalOpen} setOpen={setModalOpen}>
                <Typography2 component="p" variant="bodyPrimary">
                  {_t(
                    'We asked all learners to give feedback on our instructors based on the quality of their teaching style.'
                  )}
                </Typography2>
              </Modal>
            </div>
            <Rating rating={averageInstructorRating} ratingCount={instructorRatingCount} />
          </div>
        )}
      </div>
      <div key={instructor.id} css={styles.item}>
        <InstructorItem
          instructor={{
            ...instructor,
            fullName: partner.name,
          }}
          relatedPartnerName={getRelatedPartnerName(instructor.id, [partner])}
          disableCTA={false}
          trackingLocation={trackingLocation}
        />
      </div>
    </div>
  );
};

export default InstructorAndPartnerInfoGoogle;
